import { api } from "./apiSlice";
import * as FileSaver from "file-saver";
import { showMessage } from "../../helpers";

export const salesReportApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // API Sales Reports
    getApiSalesReport: builder.query({
      query: (quarter_name) =>
        `/template/sales/api?all_category_products=true${quarter_name ? `&quarter=${quarter_name}` : ""}`,
      providesTags: ["SalesReportAPI"],
    }),

    // FDF Sales Reports
    getFdfSalesReport: builder.query({
      query: (quarter_name) =>
        `/template/sales/fdf?all_category_products=true${quarter_name ? `&quarter=${quarter_name}` : ""}`,
      providesTags: ["SalesReportFDF"],
    }),

    // Consolidated Reports - Using POST with limit/offset in query params
    getConsolidatedApiReport: builder.query({
      query: (params = {}) => {
        const { limit = 10, offset = 0, ...bodyParams } = params;
        const queryParams = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        });
        return {
          url: `/report/sales/api?${queryParams}`,
          method: "POST",
          body: bodyParams,
        };
      },
      providesTags: ["SalesReport"],
    }),

    getConsolidatedFdfReport: builder.query({
      query: (params = {}) => {
        const { limit = 10, offset = 0, ...bodyParams } = params;
        const queryParams = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        });
        return {
          url: `/report/sales/fdf?${queryParams}`,
          method: "POST",
          body: bodyParams,
        };
      },
      providesTags: ["SalesReport"],
    }),

    getConsolidatedDiagnosticsReport: builder.query({
      query: (params = {}) => {
        const { limit = 10, offset = 0, ...bodyParams } = params;
        const queryParams = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        });
        return {
          url: `/report/sales/diagnostics?${queryParams}`,
          method: "POST",
          body: bodyParams,
        };
      },
      providesTags: ["SalesReport"],
    }),

    downloadConsolidatedApiReport: builder.mutation({
      query: (params = {}) => ({
        url: "/report/sales/download/api",
        method: "POST",
        body: params,
        responseHandler: async (response) => {
          return await response.blob();
        },
      }),
    }),

    downloadConsolidatedFdfReport: builder.mutation({
      query: (params = {}) => ({
        url: "/report/sales/download/fdf",
        method: "POST",
        body: params,
        responseHandler: async (response) => {
          return await response.blob();
        },
      }),
    }),

    downloadConsolidatedDiagnosticsReport: builder.mutation({
      query: (params = {}) => ({
        url: "/report/sales/download/diagnostics",
        method: "POST",
        body: params,
        responseHandler: async (response) => {
          return await response.blob();
        },
      }),
    }),

    // Download Reports
    downloadApiSalesReport: builder.mutation({
      query: (requestObj) => ({
        url: "/report/sales/download/api",
        method: "POST",
        body: requestObj,
        responseHandler: async (response) => {
          return await response.blob();
        },
      }),
    }),

    downloadFdfSalesReport: builder.mutation({
      query: (requestObj) => ({
        url: "/report/sales/download/fdf",
        method: "POST",
        body: requestObj,
        responseHandler: async (response) => {
          return await response.blob();
        },
      }),
    }),

    // Post Sales Reports
    postApiSalesReport: builder.mutation({
      query: (data) => ({
        url: "/template/sales/api",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SalesReportAPI"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    postFdfSalesReport: builder.mutation({
      query: (data) => ({
        url: "/template/sales/fdf",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SalesReportFDF"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    // Admin Access
    getAdminApiSalesReport: builder.query({
      query: ({ id, quarter_name }) =>
        `/template/sales/api/${id}?all_category_products=true${quarter_name ? `&quarter=${quarter_name}` : ""}`,
      providesTags: (result, error, { id }) => [{ type: "SalesReportAPI", id }],
    }),

    getAdminFdfSalesReport: builder.query({
      query: ({ id, quarter_name }) =>
        `/template/sales/fdf/${id}?all_category_products=true${quarter_name ? `&quarter=${quarter_name}` : ""}`,
      providesTags: (result, error, { id }) => [{ type: "SalesReportFDF", id }],
    }),

    postAdminApiSalesReport: builder.mutation({
      query: ({ id, data, quarter_name }) => ({
        url: `/template/sales/api/${id}${quarter_name ? `?quarter=${quarter_name}` : ""}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SalesReportAPI", id },
        "SalesReportAPI",
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    postAdminFdfSalesReport: builder.mutation({
      query: ({ id, data, quarter_name }) => ({
        url: `/template/sales/fdf/${id}${quarter_name ? `?quarter=${quarter_name}` : ""}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SalesReportFDF", id },
        "SalesReportFDF",
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    // Approval and Submit
    approveSalesReport: builder.mutation({
      query: (data) => ({
        url: "/template/sales/message/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SalesReport", "SalesReportAPI", "SalesReportFDF"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    submitSalesReport: builder.mutation({
      query: ({ message }) => ({
        url: "/template/sales/submit/",
        method: "POST",
        body: { message },
      }),
      invalidatesTags: ["SalesReport", "SalesReportAPI", "SalesReportFDF"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Reports submitted successfully!");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    markSalesReportMessageRead: builder.mutation({
      query: (type) => ({
        url: "/template/sales/mark-message-read/",
        method: "POST",
        body: { type },
      }),
      invalidatesTags: ["SalesReport"],
    }),

    // Product Verification
    getProductsToBeVerified: builder.query({
      query: () => "/admin/product-to-be-verified/",
      providesTags: ["ProductVerification"],
    }),

    postProductsToBeVerified: builder.mutation({
      query: (data) => ({
        url: "/admin/product-to-be-verified/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ProductVerification"],
    }),

    // Royalty Sales Request
    getRoyaltySalesRequest: builder.query({
      query: ({ isApproved = false, partner_id = null, ordering = null } = {}) => {
        const params = new URLSearchParams();
        if (isApproved) params.append('is_approved', isApproved);
        if (partner_id) params.append('partner_id', partner_id);
        if (ordering) params.append('ordering', ordering);
        
        const queryString = params.toString();
        return `/partner/royalty-sales-request/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ["RoyaltySalesRequest"],
    }),

  }),
});

export const {
  useGetApiSalesReportQuery,
  useGetFdfSalesReportQuery,
  useGetConsolidatedApiReportQuery,
  useGetConsolidatedFdfReportQuery,
  useGetConsolidatedDiagnosticsReportQuery,
  useDownloadConsolidatedApiReportMutation,
  useDownloadConsolidatedFdfReportMutation,
  useDownloadConsolidatedDiagnosticsReportMutation,
  useDownloadApiSalesReportMutation,
  useDownloadFdfSalesReportMutation,
  usePostApiSalesReportMutation,
  usePostFdfSalesReportMutation,
  useGetAdminApiSalesReportQuery,
  useGetAdminFdfSalesReportQuery,
  usePostAdminApiSalesReportMutation,
  usePostAdminFdfSalesReportMutation,
  useApproveSalesReportMutation,
  useSubmitSalesReportMutation,
  useMarkSalesReportMessageReadMutation,
  useGetProductsToBeVerifiedQuery,
  usePostProductsToBeVerifiedMutation,
  useGetRoyaltySalesRequestQuery
} = salesReportApi;
